/**
 * SystemSettingsPage - Manage system-wide scheduling defaults
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  getSystemSettings,
  updateSystemSettings,
  getEmailAccounts,
  getEmailAuthUrl,
  disconnectEmailAccount,
  updateEmailAccountDisplayName,
  sendTestEmail,
  runEmailHealthCheck,
  getAdminInvites,
  createAdminInvite,
  revokeAdminInvite,
  removeAdmin,
} from '../../../api/admin';
import type { EmailAccount, AdminInvite } from '../../../api/admin';
import { useAuth } from '../../../context/AuthContext';
import { isSuperAdmin } from '../../../lib/super-admins';
import ConfirmModal from '../../../components/ui/ConfirmModal';

type EmailRole = 'system' | 'event_manager';
type TestStatus = 'idle' | 'sending' | 'success' | 'error';

/** "Nov 4, 9:35 AM ET" — the team works in Eastern time. */
function formatEtDateTime(iso: string): string {
  return `${new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} ET`;
}

function formatEtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Green check normally; red cross when Google is rejecting the login. */
function AccountStatusIcon({ account }: { account: EmailAccount }) {
  if (account.health_status === 'failing') {
    return (
      <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-danger-100 flex items-center justify-center">
        <svg className="w-3 h-3 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    );
  }
  return (
    <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-success-100 flex items-center justify-center">
      <svg className="w-3 h-3 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

/** One-line result of the last Google login check. The failing case is the banner below. */
function EmailHealthLine({ account }: { account: EmailAccount }) {
  const checked = account.health_checked_at ? formatEtDateTime(account.health_checked_at) : null;
  switch (account.health_status) {
    case 'ok':
      return (
        <p className="text-sm text-success-700" data-testid={`email-health-${account.role}`}>
          Working{checked ? ` · checked ${checked}` : ''}
        </p>
      );
    case 'unknown':
      return (
        <p className="text-sm text-warning-700" data-testid={`email-health-${account.role}`}>
          Couldn't verify (Google error) · will retry{checked ? ` · last tried ${checked}` : ''}
        </p>
      );
    case 'failing':
      return null;
    default:
      return (
        <p className="text-sm text-warm-400" data-testid={`email-health-${account.role}`}>
          Login not checked yet
        </p>
      );
  }
}

/** Red banner + Reconnect when Google is rejecting this account's login. */
function EmailHealthBanner({
  account,
  onReconnect,
  reconnecting,
}: {
  account: EmailAccount;
  onReconnect: () => void;
  reconnecting: boolean;
}) {
  if (account.health_status !== 'failing') return null;
  const since = account.health_failing_since ?? account.health_checked_at;
  return (
    <div role="alert" className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg">
      <p className="text-sm text-danger-800">
        <strong>Google rejected this account's login{since ? ` on ${formatEtDate(since)}` : ''}.</strong>{' '}
        Emails sent from it are failing until {account.email} reconnects it.
      </p>
      <p className="mt-1 text-xs text-danger-700">
        To fix it, sign in to the admin as the owner of {account.email}, click Reconnect, and sign in to Google with {account.email}.
      </p>
      <button
        type="button"
        onClick={onReconnect}
        disabled={reconnecting}
        className="mt-3 px-4 py-2 text-sm font-medium rounded-lg bg-danger-600 text-white hover:bg-danger-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {reconnecting ? 'Connecting...' : 'Reconnect'}
      </button>
    </div>
  );
}

export default function SystemSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [defaultMatineeTime, setDefaultMatineeTime] = useState('14:00');
  const [matineeThresholdTime, setMatineeThresholdTime] = useState('13:00');
  const [defaultSessionStartTime, setDefaultSessionStartTime] = useState('09:00');
  const [sessionDurationMinutes, setSessionDurationMinutes] = useState(90);
  const [adminNotificationEmail, setAdminNotificationEmail] = useState('');
  const [inquiryNotificationEmail, setInquiryNotificationEmail] = useState('');
  const [campaignAutoQueueDaysBefore, setCampaignAutoQueueDaysBefore] = useState(90);
  const [campaignCadenceWarningDays, setCampaignCadenceWarningDays] = useState(14);
  const [campaignReminderIntervalDays, setCampaignReminderIntervalDays] = useState(30);
  const [campaignFinalReminderDaysBefore, setCampaignFinalReminderDaysBefore] = useState(60);
  const [campaignDailySendLimit, setCampaignDailySendLimit] = useState(900);

  // Email account state
  const [emailAccount, setEmailAccount] = useState<EmailAccount | null>(null);
  const [eventManagerAccount, setEventManagerAccount] = useState<EmailAccount | null>(null);
  const [emailLoading, setEmailLoading] = useState(true);
  const [emailConnecting, setEmailConnecting] = useState(false);
  const [emailDisconnecting, setEmailDisconnecting] = useState(false);
  const [emConnecting, setEmConnecting] = useState(false);
  const [emDisconnecting, setEmDisconnecting] = useState(false);
  const [emDisplayName, setEmDisplayName] = useState('');
  const [emNameSaving, setEmNameSaving] = useState(false);
  const [testStatus, setTestStatus] = useState<Record<EmailRole, TestStatus>>({
    system: 'idle',
    event_manager: 'idle',
  });
  const [healthChecking, setHealthChecking] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);

  // Admin invites (super-admin allowlist gates this section)
  const { user } = useAuth();
  const showAdminInvites = isSuperAdmin(user?.email);
  const [adminInvites, setAdminInvites] = useState<AdminInvite[]>([]);
  const [adminInvitesLoading, setAdminInvitesLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    { kind: 'revoke' | 'remove'; invite: AdminInvite } | null
  >(null);
  const [confirmRunning, setConfirmRunning] = useState(false);

  useEffect(() => {
    loadSettings();
    loadEmailAccounts();
    if (showAdminInvites) {
      loadAdminInvites();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAdminInvites]);

  // Check for email_connected query param on mount
  useEffect(() => {
    if (searchParams.get('email_connected') === 'true') {
      setEmailSuccess('Email account connected successfully!');
      searchParams.delete('email_connected');
      setSearchParams(searchParams, { replace: true });
      setTimeout(() => setEmailSuccess(null), 5000);
    }
  }, [searchParams, setSearchParams]);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await getSystemSettings();
      // Strip seconds from time values for <input type="time"> (HH:MM format)
      setDefaultMatineeTime(data.default_matinee_time.slice(0, 5));
      setMatineeThresholdTime(data.matinee_threshold_time.slice(0, 5));
      setDefaultSessionStartTime(data.default_session_start_time.slice(0, 5));
      setSessionDurationMinutes(data.session_duration_minutes);
      setAdminNotificationEmail(data.admin_notification_email || '');
      setInquiryNotificationEmail(data.inquiry_notification_email || '');
      setCampaignAutoQueueDaysBefore(data.campaign_auto_queue_days_before ?? 90);
      setCampaignCadenceWarningDays(data.campaign_cadence_warning_days ?? 14);
      setCampaignReminderIntervalDays(data.campaign_reminder_interval_days ?? 30);
      setCampaignFinalReminderDaysBefore(data.campaign_final_reminder_days_before ?? 60);
      setCampaignDailySendLimit(data.campaign_daily_send_limit ?? 900);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  /** `quiet` refreshes the cards in place (after Check now / a test send) without the skeleton. */
  const loadEmailAccounts = async (quiet = false) => {
    try {
      if (!quiet) setEmailLoading(true);
      const accounts = await getEmailAccounts();
      setEmailAccount(accounts.find((a) => a.role === 'system') ?? null);
      const emAccount = accounts.find((a) => a.role === 'event_manager') ?? null;
      setEventManagerAccount(emAccount);
      if (emAccount?.display_name && !quiet) setEmDisplayName(emAccount.display_name);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to load email accounts');
    } finally {
      setEmailLoading(false);
    }
  };

  const loadAdminInvites = async () => {
    try {
      setAdminInvitesLoading(true);
      const invites = await getAdminInvites();
      setAdminInvites(invites);
    } catch (err) {
      console.error('Failed to load admin invites:', err);
    } finally {
      setAdminInvitesLoading(false);
    }
  };

  const handleInviteAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteError(null);
    setInviteSuccess(null);
    setInviteSending(true);
    try {
      const result = await createAdminInvite(inviteEmail.trim());
      setInviteEmail('');
      if (result.emailSent) {
        setInviteSuccess(`Invitation sent to ${result.invite.email}.`);
      } else {
        setInviteSuccess(
          `Invitation created for ${result.invite.email}, but no email was sent: ${result.emailWarning ?? 'no Gmail account connected'}`
        );
      }
      await loadAdminInvites();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setInviteSending(false);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction || confirmRunning) return;
    const { kind, invite } = confirmAction;
    setInviteError(null);
    setConfirmRunning(true);
    try {
      if (kind === 'revoke') {
        await revokeAdminInvite(invite.id);
      } else if (invite.accepted_user_id) {
        await removeAdmin(invite.accepted_user_id);
      }
      await loadAdminInvites();
      setConfirmAction(null);
    } catch (err) {
      setInviteError(
        err instanceof Error
          ? err.message
          : kind === 'revoke'
          ? 'Failed to revoke invitation'
          : 'Failed to remove admin'
      );
      setConfirmAction(null);
    } finally {
      setConfirmRunning(false);
    }
  };

  const handleConnectEmail = async (role: string = 'system') => {
    try {
      if (role === 'event_manager') {
        setEmConnecting(true);
      } else {
        setEmailConnecting(true);
      }
      setEmailError(null);
      const { url, state } = await getEmailAuthUrl(role);
      localStorage.setItem('email_oauth_state', state);
      window.location.href = url;
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to start email connection');
      setEmailConnecting(false);
      setEmConnecting(false);
    }
  };

  const handleDisconnectEmail = async (account: EmailAccount) => {
    try {
      if (account.role === 'event_manager') {
        setEmDisconnecting(true);
      } else {
        setEmailDisconnecting(true);
      }
      setEmailError(null);
      await disconnectEmailAccount(account.id);
      if (account.role === 'event_manager') {
        setEventManagerAccount(null);
      } else {
        setEmailAccount(null);
      }
      setEmailSuccess('Email account disconnected.');
      setTimeout(() => setEmailSuccess(null), 3000);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to disconnect email account');
    } finally {
      setEmailDisconnecting(false);
      setEmDisconnecting(false);
    }
  };

  const setRoleTestStatus = (role: EmailRole, status: TestStatus) =>
    setTestStatus((prev) => ({ ...prev, [role]: status }));

  const handleSendTestEmail = async (role: EmailRole) => {
    try {
      setRoleTestStatus(role, 'sending');
      setEmailError(null);
      await sendTestEmail(role);
      setRoleTestStatus(role, 'success');
      setTimeout(() => setRoleTestStatus(role, 'idle'), 3000);
    } catch (err) {
      setRoleTestStatus(role, 'error');
      setEmailError(err instanceof Error ? err.message : 'Failed to send test email');
      setTimeout(() => setRoleTestStatus(role, 'idle'), 3000);
    } finally {
      // The test send records the account's health — refresh the status lines.
      void loadEmailAccounts(true);
    }
  };

  /** "Check now": ask Google whether each connected login still works, then refresh the cards. */
  const handleCheckNow = async () => {
    try {
      setHealthChecking(true);
      setEmailError(null);
      setEmailSuccess(null);
      const results = await runEmailHealthCheck();
      await loadEmailAccounts(true);
      const failing = results.filter((r) => r.health_status === 'failing');
      const unknown = results.filter((r) => r.health_status === 'unknown');
      if (results.length === 0) {
        setEmailSuccess('No connected email accounts to check.');
      } else if (failing.length > 0) {
        setEmailError(
          `Check complete: ${failing.map((r) => r.email).join(', ')} ${failing.length === 1 ? 'needs' : 'need'} to be reconnected (see below).`
        );
      } else if (unknown.length > 0) {
        setEmailSuccess(
          `Check complete: Google couldn't be reached for ${unknown.map((r) => r.email).join(', ')}. It will retry tomorrow at 8 AM ET.`
        );
      } else {
        setEmailSuccess(
          results.length === 1 ? 'Check complete: the email account is working.' : `Check complete: all ${results.length} email accounts are working.`
        );
      }
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to check email accounts');
    } finally {
      setHealthChecking(false);
    }
  };

  const testButtonLabel = (role: EmailRole): string => {
    const status = testStatus[role];
    return status === 'sending'
      ? 'Sending...'
      : status === 'success'
        ? 'Test Email Sent!'
        : status === 'error'
          ? 'Failed to Send'
          : 'Send Test Email';
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    try {
      setIsSaving(true);
      await updateSystemSettings({
        default_matinee_time: defaultMatineeTime,
        matinee_threshold_time: matineeThresholdTime,
        default_session_start_time: defaultSessionStartTime,
        session_duration_minutes: sessionDurationMinutes,
        admin_notification_email: adminNotificationEmail || undefined,
        inquiry_notification_email: inquiryNotificationEmail || undefined,
        campaign_auto_queue_days_before: campaignAutoQueueDaysBefore,
        campaign_cadence_warning_days: campaignCadenceWarningDays,
        campaign_reminder_interval_days: campaignReminderIntervalDays,
        campaign_final_reminder_days_before: campaignFinalReminderDaysBefore,
        campaign_daily_send_limit: campaignDailySendLimit,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse mb-8" />
        <div className="bg-white rounded-xl shadow-sm p-8 animate-pulse">
          <div className="space-y-6">
            <div className="h-10 bg-warm-100 rounded w-full" />
            <div className="h-10 bg-warm-100 rounded w-full" />
            <div className="h-10 bg-warm-100 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
          System Settings
        </h1>
        <p className="mt-1 text-warm-500">
          Configure default scheduling parameters for all tour stops and bookings.
        </p>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Email Account Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-warm-900">
              Email Account
            </h2>
            {!emailLoading && (emailAccount || eventManagerAccount) && (
              <button
                type="button"
                onClick={handleCheckNow}
                disabled={healthChecking}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-warm-200 text-warm-700 hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {healthChecking ? 'Checking...' : 'Check now'}
              </button>
            )}
          </div>

          {emailSuccess && (
            <div className="mb-4 p-4 bg-success-50 border border-success-200 rounded-lg text-success-700">
              {emailSuccess}
            </div>
          )}

          {emailError && (
            <div className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
              {emailError}
            </div>
          )}

          {emailLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-5 bg-warm-100 rounded w-3/4" />
              <div className="h-5 bg-warm-100 rounded w-1/2" />
            </div>
          ) : emailAccount ? (
            <div>
              <div className="flex items-start gap-3 mb-4">
                <AccountStatusIcon account={emailAccount} />
                <div>
                  <p className="text-sm font-medium text-warm-900">{emailAccount.email}</p>
                  <p className="text-sm text-warm-500">
                    {emailAccount.provider === 'gmail' ? 'Gmail' : emailAccount.provider} &middot; Connected {new Date(emailAccount.connected_at).toLocaleDateString()}
                  </p>
                  <EmailHealthLine account={emailAccount} />
                </div>
              </div>

              <EmailHealthBanner
                account={emailAccount}
                onReconnect={() => handleConnectEmail('system')}
                reconnecting={emailConnecting}
              />

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleSendTestEmail('system')}
                  disabled={testStatus.system === 'sending'}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-warm-200 text-warm-700 hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testButtonLabel('system')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDisconnectEmail(emailAccount!)}
                  disabled={emailDisconnecting}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-danger-200 text-danger-600 hover:bg-danger-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emailDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm text-warm-500 mb-4">
                No email account connected. <strong>No emails will be sent</strong> until you connect a Gmail account below.
              </p>
              <button
                type="button"
                onClick={() => handleConnectEmail('system')}
                disabled={emailConnecting}
                className="btn-primary"
              >
                {emailConnecting ? 'Connecting...' : 'Connect Gmail Account'}
              </button>
            </div>
          )}
        </div>

        {/* Team Members (super-admin only) */}
        {showAdminInvites && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-warm-900 mb-1">Team Members</h2>
            <p className="text-sm text-warm-500 mb-4">
              Invite a new admin by email. They'll receive a one-time link to set their password and activate the account. Invitations expire in 7 days.
            </p>

            <form onSubmit={handleInviteAdmin} className="flex flex-col sm:flex-row gap-2 mb-4">
              <input
                type="email"
                required
                placeholder="newadmin@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviteSending}
                className="flex-1 px-4 py-2 rounded-lg border border-warm-200 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-warm-50"
              />
              <button
                type="submit"
                disabled={inviteSending || !inviteEmail.trim()}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inviteSending ? 'Sending…' : 'Send Invitation'}
              </button>
            </form>

            {inviteError && (
              <div className="mb-3 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-800">
                {inviteError}
              </div>
            )}
            {inviteSuccess && (
              <div className="mb-3 p-3 bg-success-50 border border-success-200 rounded-lg text-sm text-success-800">
                {inviteSuccess}
              </div>
            )}

            {adminInvitesLoading ? (
              <div className="animate-pulse space-y-2">
                <div className="h-5 bg-warm-100 rounded w-3/4" />
                <div className="h-5 bg-warm-100 rounded w-1/2" />
              </div>
            ) : adminInvites.length === 0 ? (
              <p className="text-sm text-warm-500 italic">No invitations sent yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-warm-500 border-b border-warm-100">
                      <th className="py-2 pr-4 font-medium">Email</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 pr-4 font-medium">Sent</th>
                      <th className="py-2 pr-4 font-medium">Expires</th>
                      <th className="py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminInvites.map((inv) => (
                      <tr key={inv.id} className="border-b border-warm-50 last:border-0">
                        <td className="py-2 pr-4 text-warm-900">{inv.email}</td>
                        <td className="py-2 pr-4">
                          <span
                            className={
                              inv.status === 'pending'
                                ? 'inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800'
                                : inv.status === 'accepted'
                                ? 'inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800'
                                : 'inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-warm-100 text-warm-700'
                            }
                          >
                            {inv.status}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-warm-500">{new Date(inv.invited_at).toLocaleDateString()}</td>
                        <td className="py-2 pr-4 text-warm-500">{new Date(inv.expires_at).toLocaleDateString()}</td>
                        <td className="py-2 text-right">
                          {inv.status === 'pending' ? (
                            <button
                              type="button"
                              onClick={() => setConfirmAction({ kind: 'revoke', invite: inv })}
                              className="text-xs font-medium text-danger-600 hover:text-danger-700"
                            >
                              Revoke
                            </button>
                          ) : inv.status === 'accepted' && inv.accepted_user_id && !isSuperAdmin(inv.email) ? (
                            <button
                              type="button"
                              onClick={() => setConfirmAction({ kind: 'remove', invite: inv })}
                              className="text-xs font-medium text-danger-600 hover:text-danger-700"
                            >
                              Remove admin
                            </button>
                          ) : (
                            <span className="text-xs text-warm-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Revoke / Remove admin confirmation (SDA-220) */}
        {confirmAction && (
          <ConfirmModal
            title={confirmAction.kind === 'revoke' ? 'Revoke Invitation' : 'Remove Admin'}
            message={
              confirmAction.kind === 'revoke'
                ? `Revoke the pending invitation for ${confirmAction.invite.email}? The one-time link will stop working.`
                : `Remove admin access for ${confirmAction.invite.email}? They will lose access immediately and be signed out on their next action.`
            }
            confirmLabel={confirmAction.kind === 'revoke' ? 'Revoke' : 'Remove admin'}
            confirmVariant="danger"
            onConfirm={handleConfirmAction}
            onCancel={() => {
              if (!confirmRunning) setConfirmAction(null);
            }}
          />
        )}

        {/* Event Manager Email Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-warm-900 mb-1">
            Event Manager Email
          </h2>
          <p className="text-sm text-warm-500 mb-4">
            Personal email for event manager introduction messages. Sent automatically when a booking is confirmed.
          </p>

          {emailLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-5 bg-warm-100 rounded w-3/4" />
              <div className="h-5 bg-warm-100 rounded w-1/2" />
            </div>
          ) : eventManagerAccount ? (
            <div>
              <div className="flex items-start gap-3 mb-4">
                <AccountStatusIcon account={eventManagerAccount} />
                <div>
                  <p className="text-sm font-medium text-warm-900">{eventManagerAccount.email}</p>
                  {eventManagerAccount.health_status !== 'failing' && (
                    <p className="text-sm text-success-600">
                      Active — Introduction emails will be sent from this address when bookings are confirmed.
                    </p>
                  )}
                  <EmailHealthLine account={eventManagerAccount} />
                </div>
              </div>

              <EmailHealthBanner
                account={eventManagerAccount}
                onReconnect={() => handleConnectEmail('event_manager')}
                reconnecting={emConnecting}
              />

              {/* Display Name */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-warm-700 mb-1">
                  Display Name
                </label>
                <p className="text-xs text-warm-500 mb-2">
                  This name appears in the introduction email (e.g. "Hi, I'm <strong>{emDisplayName}</strong>, your event manager...")
                </p>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={emDisplayName}
                    onChange={(e) => setEmDisplayName(e.target.value)}
                    placeholder="Event manager name"
                    className="w-48 px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  />
                  <button
                    type="button"
                    disabled={emNameSaving || !emDisplayName.trim() || emDisplayName === (eventManagerAccount.display_name || '')}
                    onClick={async () => {
                      try {
                        setEmNameSaving(true);
                        setEmailError(null);
                        const updated = await updateEmailAccountDisplayName(eventManagerAccount.id, emDisplayName.trim());
                        setEventManagerAccount({ ...eventManagerAccount, display_name: updated.display_name });
                        setEmailSuccess('Display name updated.');
                        setTimeout(() => setEmailSuccess(null), 3000);
                      } catch (err) {
                        setEmailError(err instanceof Error ? err.message : 'Failed to update name');
                      } finally {
                        setEmNameSaving(false);
                      }
                    }}
                    className="px-3 py-2 text-sm font-medium rounded-lg border border-warm-200 text-warm-700 hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {emNameSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleSendTestEmail('event_manager')}
                  disabled={testStatus.event_manager === 'sending'}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-warm-200 text-warm-700 hover:bg-warm-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testButtonLabel('event_manager')}
                </button>
                <button
                  type="button"
                  onClick={() => handleDisconnectEmail(eventManagerAccount)}
                  disabled={emDisconnecting}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-danger-200 text-danger-600 hover:bg-danger-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="p-4 bg-warm-50 border border-warm-200 rounded-lg mb-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-warm-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm text-warm-600">
                    Event manager intro emails are currently <strong>disabled</strong>. Connect a Gmail account to enable automatic introduction emails on booking confirmation.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleConnectEmail('event_manager')}
                disabled={emConnecting}
                className="btn-primary"
              >
                {emConnecting ? 'Connecting...' : 'Connect Event Manager Gmail'}
              </button>
            </div>
          )}
        </div>

        {/* Settings Form */}
        <form onSubmit={handleSave}>
          <div className="space-y-6">
            {error && (
              <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
                {error}
              </div>
            )}

            {success && (
              <div className="p-4 bg-success-50 border border-success-200 rounded-lg text-success-700">
                Settings saved successfully.
              </div>
            )}

            {/* Session Start Time */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Session Timing
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Default Session Start Time
                  </label>
                  <input
                    type="time"
                    value={defaultSessionStartTime}
                    onChange={(e) => setDefaultSessionStartTime(e.target.value)}
                    className="w-48 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    The default start time for Session 1. Studios can choose 30 minutes early or late.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Session Duration (minutes)
                  </label>
                  <input
                    type="number"
                    value={sessionDurationMinutes}
                    onChange={(e) => setSessionDurationMinutes(parseInt(e.target.value) || 90)}
                    min={30}
                    max={180}
                    step={15}
                    className="w-32 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    Duration of each class session (30-180 minutes).
                  </p>
                </div>
              </div>
            </div>

            {/* Matinee Settings */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Matinee & Simultaneous Sessions
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Default Matinee Time
                  </label>
                  <input
                    type="time"
                    value={defaultMatineeTime}
                    onChange={(e) => setDefaultMatineeTime(e.target.value)}
                    className="w-48 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    The default matinee time for shows. Used when a tour stop doesn't have a custom matinee time set.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Matinee Threshold Time
                  </label>
                  <input
                    type="time"
                    value={matineeThresholdTime}
                    onChange={(e) => setMatineeThresholdTime(e.target.value)}
                    className="w-48 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    When the matinee is at or before this time, both sessions automatically run simultaneously
                    (same start time) instead of sequentially. This ensures faculty can attend the matinee performance.
                  </p>
                </div>
              </div>

              <div className="mt-4 p-4 bg-warm-50 rounded-lg">
                <h3 className="text-sm font-medium text-warm-700 mb-2">How it works</h3>
                <ul className="text-sm text-warm-600 space-y-1.5 list-disc ml-4">
                  <li>
                    <strong>Sequential sessions:</strong> Session 2 starts immediately after Session 1 ends (no gap).
                  </li>
                  <li>
                    <strong>Simultaneous sessions:</strong> Both sessions start at the same time when triggered by an early matinee or manual override.
                  </li>
                  <li>
                    Each tour stop can override the default matinee time. If not set, the system default above is used.
                  </li>
                </ul>
              </div>
            </div>

            {/* Campaign Auto-Queue */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Campaign Auto-Queue
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Days Before Tour Start
                  </label>
                  <input
                    type="number"
                    value={campaignAutoQueueDaysBefore}
                    onChange={(e) => setCampaignAutoQueueDaysBefore(parseInt(e.target.value) || 90)}
                    min={1}
                    max={365}
                    className="w-32 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    How far in advance campaigns are auto-generated for upcoming tour stops
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Monthly Reminder Cadence (days)
                  </label>
                  <input
                    type="number"
                    value={campaignReminderIntervalDays}
                    onChange={(e) => setCampaignReminderIntervalDays(parseInt(e.target.value) || 30)}
                    min={7}
                    max={90}
                    className="w-32 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    Days between monthly reminders, between the announcement and the final reminder.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Final Reminder Lead Time (days)
                  </label>
                  <input
                    type="number"
                    value={campaignFinalReminderDaysBefore}
                    onChange={(e) => setCampaignFinalReminderDaysBefore(parseInt(e.target.value) || 60)}
                    min={1}
                    max={180}
                    className="w-32 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    Days before tour start to send the final reminder.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Send Limit (rolling 24h)
                  </label>
                  <input
                    type="number"
                    value={campaignDailySendLimit}
                    onChange={(e) => setCampaignDailySendLimit(parseInt(e.target.value) || 900)}
                    min={1}
                    max={50000}
                    className="w-32 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    Maximum emails the system will send per rolling 24-hour window before pausing. Do not raise above 1,700 — Gmail's hard cap is 2,000 per rolling 24 hours, and transactional email and burst overshoot consume the remaining headroom.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Cadence Warning (days)
                  </label>
                  <input
                    type="number"
                    value={campaignCadenceWarningDays}
                    onChange={(e) => setCampaignCadenceWarningDays(parseInt(e.target.value) || 14)}
                    min={1}
                    max={60}
                    className="w-32 px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    Show a warning if another campaign was sent to the same area within this many days
                  </p>
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Notifications
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Inquiry Notification Email
                  </label>
                  <input
                    type="email"
                    value={inquiryNotificationEmail}
                    onChange={(e) => setInquiryNotificationEmail(e.target.value)}
                    placeholder="admin@stagedooraccess.com"
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    Email address that receives notifications when new inquiries, contact form submissions, or SLA alerts are triggered. Defaults to admin@stagedooraccess.com if left empty.
                  </p>
                </div>
              </div>
            </div>

            {/* Marketing Materials */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Marketing Materials
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Admin Notification Email
                  </label>
                  <input
                    type="email"
                    value={adminNotificationEmail}
                    onChange={(e) => setAdminNotificationEmail(e.target.value)}
                    placeholder="info@stagedooraccess.com"
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-sm text-warm-500">
                    Email address that receives notifications when marketing materials are submitted or a booking with marketing materials is confirmed.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              <button
                type="submit"
                disabled={isSaving}
                className="btn-primary"
              >
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
              <Link
                to="/admin"
                className="text-warm-600 hover:text-warm-900 text-sm font-medium"
              >
                Cancel
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
